import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { User, Trash2, Download, Plus, X, Upload, FileText, GraduationCap, ShieldCheck, Target as TargetIcon, Briefcase } from "lucide-react";
import { endpoints, API } from "@/lib/api";
import { toast } from "sonner";

const LEVEL_TONE = {
  None: "bg-slate-100 text-slate-500 border-slate-200",
  Novice: "bg-slate-100 text-slate-700 border-slate-200",
  Intermediate: "bg-blue-50 text-blue-700 border-blue-200",
  Proficient: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Expert: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const TRAINING_TONE = {
  Valid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Expiring: "bg-amber-50 text-amber-700 border-amber-200",
  Expired: "bg-rose-50 text-rose-700 border-rose-200",
};

const empty = {
  client_slug: "", name: "", position: "", department: "", email: "", phone: "",
  job_description: "", required_competencies: [], is_active: true,
};

export default function EmployeeModal({ open, employeeId, defaultClientSlug, clients = [], onOpenChange, onChanged }) {
  const [form, setForm] = useState(empty);
  const [loaded, setLoaded] = useState(null);
  const [levels, setLevels] = useState(["None", "Novice", "Intermediate", "Proficient", "Expert"]);
  const [tab, setTab] = useState("profile");
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState({ name: "" });
  const [newTraining, setNewTraining] = useState({ name: "", provider: "", issue_date: "", expiry_date: "" });
  const [newReqComp, setNewReqComp] = useState("");
  const [newCurComp, setNewCurComp] = useState({ name: "", level: "Novice" });
  const [newQual, setNewQual] = useState({ name: "", institution: "", year: "" });
  const [newAppt, setNewAppt] = useState({ role: "", appointed_date: "", reference: "" });
  const [newKpi, setNewKpi] = useState({ name: "", target: "", current: "" });
  const certFileRef = useRef(null);
  const [uploadingCertFor, setUploadingCertFor] = useState(null);
  const isEdit = !!employeeId;

  useEffect(() => {
    if (!open) return;
    endpoints.employeeMetadata().then((m) => setLevels(m.levels || levels)).catch(() => {});
    endpoints.me().then(setMe).catch(() => {});
    if (isEdit) {
      endpoints.getEmployee(employeeId).then((e) => { setLoaded(e); setForm({ ...empty, ...e }); setTab("profile"); });
    } else {
      setLoaded(null);
      setForm({ ...empty, client_slug: defaultClientSlug || clients[0]?.slug || "" });
      setTab("profile");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employeeId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const refresh = async () => {
    const e = await endpoints.getEmployee(employeeId);
    setLoaded(e); setForm({ ...empty, ...e });
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return toast.error("Employee name is required");
    if (!form.client_slug) return toast.error("Select a client");
    setSaving(true);
    try {
      if (isEdit) {
        const { client_slug: _cs, current_competencies, qualifications, legal_appointments, kpis, ...rest } = form;
        await endpoints.updateEmployee(employeeId, {
          ...rest,
          current_competencies: loaded.current_competencies,
          qualifications: loaded.qualifications,
          legal_appointments: loaded.legal_appointments,
          kpis: loaded.kpis,
        });
        toast.success("Employee updated");
      } else {
        await endpoints.createEmployee(form);
        toast.success("Employee added");
      }
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${loaded?.name}? This cannot be undone.`)) return;
    try { await endpoints.deleteEmployee(employeeId); toast.success("Deleted"); onChanged?.(); }
    catch { toast.error("Could not delete"); }
  };

  // Nested editors
  const updateNested = async (field, value) => {
    setLoaded((l) => ({ ...l, [field]: value }));
    try { await endpoints.updateEmployee(employeeId, { [field]: value }); }
    catch { toast.error("Could not save"); refresh(); }
  };

  const addReqComp = async () => {
    if (!newReqComp.trim()) return;
    const next = [...(loaded.required_competencies || []), newReqComp.trim()];
    setNewReqComp("");
    await updateNested("required_competencies", next);
  };
  const removeReqComp = async (name) => {
    await updateNested("required_competencies", loaded.required_competencies.filter((n) => n !== name));
  };
  const addCurComp = async () => {
    if (!newCurComp.name.trim()) return;
    const next = [...(loaded.current_competencies || []), { ...newCurComp }];
    setNewCurComp({ name: "", level: "Novice" });
    await updateNested("current_competencies", next);
  };
  const updateCurComp = async (i, k, v) => {
    const next = loaded.current_competencies.map((c, idx) => idx === i ? { ...c, [k]: v } : c);
    await updateNested("current_competencies", next);
  };
  const removeCurComp = async (i) => {
    await updateNested("current_competencies", loaded.current_competencies.filter((_, idx) => idx !== i));
  };
  const addQual = async () => {
    if (!newQual.name.trim()) return;
    const next = [...(loaded.qualifications || []), { ...newQual, year: newQual.year ? Number(newQual.year) : null }];
    setNewQual({ name: "", institution: "", year: "" });
    await updateNested("qualifications", next);
  };
  const removeQual = async (i) => await updateNested("qualifications", loaded.qualifications.filter((_, idx) => idx !== i));
  const addAppt = async () => {
    if (!newAppt.role.trim()) return;
    const next = [...(loaded.legal_appointments || []), { ...newAppt }];
    setNewAppt({ role: "", appointed_date: "", reference: "" });
    await updateNested("legal_appointments", next);
  };
  const removeAppt = async (i) => await updateNested("legal_appointments", loaded.legal_appointments.filter((_, idx) => idx !== i));
  const addKpi = async () => {
    if (!newKpi.name.trim()) return;
    const next = [...(loaded.kpis || []), { ...newKpi }];
    setNewKpi({ name: "", target: "", current: "" });
    await updateNested("kpis", next);
  };
  const removeKpi = async (i) => await updateNested("kpis", loaded.kpis.filter((_, idx) => idx !== i));

  // Trainings
  const addTraining = async () => {
    if (!newTraining.name.trim()) return toast.error("Training name required");
    try {
      await endpoints.addTraining(employeeId, newTraining);
      setNewTraining({ name: "", provider: "", issue_date: "", expiry_date: "" });
      await refresh();
    } catch { toast.error("Could not add training"); }
  };
  const removeTraining = async (tid) => {
    if (!window.confirm("Remove this training record?")) return;
    try { await endpoints.deleteTraining(employeeId, tid); await refresh(); }
    catch { toast.error("Could not delete"); }
  };
  const uploadCertificate = async (tid, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File too large — max 5 MB");
    setUploadingCertFor(tid);
    try {
      await endpoints.uploadCertificate(employeeId, tid, file, me.name);
      toast.success("Certificate uploaded");
      await refresh();
    } catch { toast.error("Upload failed"); }
    finally { setUploadingCertFor(null); if (certFileRef.current) certFileRef.current.value = ""; }
  };

  const coverage = loaded?.competency_coverage ?? 0;
  const trainings = loaded?.trainings || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-employee" className="sm:max-w-[960px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 text-white flex items-center justify-center shadow-md shadow-purple-500/25">
              <User className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {isEdit ? (
                  <>
                    <span className="truncate">{loaded?.name || "…"}</span>
                    {loaded?.position && <Badge variant="outline" className="text-[10px] rounded-full">{loaded.position}</Badge>}
                    {loaded?.training_expiring > 0 && <Badge variant="outline" className={`text-[10px] rounded-full ${TRAINING_TONE.Expiring}`}>{loaded.training_expiring} expiring</Badge>}
                    {loaded?.training_expired > 0 && <Badge variant="outline" className={`text-[10px] rounded-full ${TRAINING_TONE.Expired}`}>{loaded.training_expired} expired</Badge>}
                  </>
                ) : "New Employee"}
              </DialogTitle>
              <DialogDescription>
                {isEdit ? `${loaded?.client_name || ""} · ${loaded?.department || ""} · Coverage ${coverage}%` : "Add an employee to this client's register."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="pt-2">
          <TabsList className="bg-slate-100 dark:bg-slate-800 mb-3 h-auto flex-wrap">
            <TabsTrigger value="profile" data-testid="emp-tab-profile" className="text-xs h-8">Profile</TabsTrigger>
            {isEdit && <TabsTrigger value="competencies" data-testid="emp-tab-competencies" className="text-xs h-8">Competencies</TabsTrigger>}
            {isEdit && <TabsTrigger value="training" data-testid="emp-tab-training" className="text-xs h-8">Training ({trainings.length})</TabsTrigger>}
            {isEdit && <TabsTrigger value="qualifications" data-testid="emp-tab-qualifications" className="text-xs h-8">Qualifications</TabsTrigger>}
            {isEdit && <TabsTrigger value="appointments" data-testid="emp-tab-appointments" className="text-xs h-8">Legal appointments</TabsTrigger>}
            {isEdit && <TabsTrigger value="kpis" data-testid="emp-tab-kpis" className="text-xs h-8">KPIs</TabsTrigger>}
          </TabsList>

          {tab === "profile" && (
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Client</Label><Select value={form.client_slug} onValueChange={(v) => set("client_slug", v)} disabled={isEdit}><SelectTrigger data-testid="emp-client-select"><SelectValue placeholder="Select client" /></SelectTrigger><SelectContent>{clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Full name</Label><Input data-testid="emp-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
                <div><Label>Position</Label><Input data-testid="emp-position-input" value={form.position} onChange={(e) => set("position", e.target.value)} /></div>
                <div><Label>Department</Label><Input data-testid="emp-dept-input" value={form.department} onChange={(e) => set("department", e.target.value)} /></div>
                <div><Label>Email</Label><Input data-testid="emp-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
                <div><Label>Phone</Label><Input data-testid="emp-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
                <div className="col-span-2"><Label>Job description</Label><Textarea rows={3} data-testid="emp-jobdesc-input" value={form.job_description} onChange={(e) => set("job_description", e.target.value)} /></div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {isEdit && <Button data-testid="emp-delete-button" type="button" variant="ghost" className="text-rose-600 mr-auto" onClick={remove}><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>}
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
                <Button data-testid="emp-submit-button" type="submit" className="brand-gradient text-white" disabled={saving}>{saving ? "Saving..." : isEdit ? "Save changes" : "Add employee"}</Button>
              </DialogFooter>
            </form>
          )}

          {tab === "competencies" && isEdit && loaded && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2"><Label className="flex items-center gap-1.5"><TargetIcon className="h-3.5 w-3.5" /> Required competencies for this role</Label></div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(loaded.required_competencies || []).map((r) => (
                    <Badge key={r} variant="outline" className="text-[10px] rounded-full pl-2 pr-1 py-0.5 gap-1">
                      {r}
                      <button type="button" onClick={() => removeReqComp(r)} className="hover:text-rose-500"><X className="h-3 w-3" /></button>
                    </Badge>
                  ))}
                  {(loaded.required_competencies || []).length === 0 && <span className="text-xs text-slate-500">Add required competencies to build a role profile.</span>}
                </div>
                <div className="flex gap-2">
                  <Input data-testid="emp-reqcomp-input" value={newReqComp} onChange={(e) => setNewReqComp(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addReqComp(); } }} placeholder="e.g. ISO 9001 Internal Auditor" />
                  <Button data-testid="emp-reqcomp-add" type="button" variant="outline" onClick={addReqComp}><Plus className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden">
                <div className="bg-slate-50/70 dark:bg-slate-900/60 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex justify-between"><span>Current competencies</span><span>Coverage {coverage}%</span></div>
                <div className="p-3 space-y-2">
                  {(loaded.current_competencies || []).map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input value={c.name} onChange={(e) => updateCurComp(i, "name", e.target.value)} className="h-8 flex-1" />
                      <Select value={c.level} onValueChange={(v) => updateCurComp(i, "level", v)}>
                        <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>{levels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => removeCurComp(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-white/5">
                    <Input data-testid="emp-newcomp-name" value={newCurComp.name} onChange={(e) => setNewCurComp({ ...newCurComp, name: e.target.value })} className="h-8 flex-1" placeholder="Competency name" />
                    <Select value={newCurComp.level} onValueChange={(v) => setNewCurComp({ ...newCurComp, level: v })}>
                      <SelectTrigger data-testid="emp-newcomp-level" className="h-8 w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>{levels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button data-testid="emp-newcomp-add" type="button" variant="outline" size="sm" onClick={addCurComp}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
                  </div>
                  {(loaded.competency_gaps || []).length > 0 && (
                    <div className="mt-2 rounded-xl bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/30 p-2 text-xs">
                      <span className="font-bold">Gaps:</span> {loaded.competency_gaps.join(", ")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === "training" && isEdit && loaded && (
            <div className="space-y-3">
              <input ref={certFileRef} type="file" hidden onChange={(e) => uploadingCertFor && uploadCertificate(uploadingCertFor, e.target.files?.[0])} />
              <div className="rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/70 dark:bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    <tr><th className="text-left px-3 py-2">Training</th><th className="text-left px-3 py-2">Provider</th><th className="text-left px-3 py-2">Issued</th><th className="text-left px-3 py-2">Expires</th><th className="text-center px-3 py-2">Status</th><th className="text-right px-3 py-2 w-32">Certificate</th></tr>
                  </thead>
                  <tbody>
                    {trainings.map((t) => (
                      <tr key={t.id} data-testid={`emp-training-${t.id}`} className={`border-t border-slate-100 dark:border-white/5 ${t.status === "Expired" ? "bg-rose-50/40 dark:bg-rose-500/10" : t.status === "Expiring" ? "bg-amber-50/40 dark:bg-amber-500/10" : ""}`}>
                        <td className="px-3 py-2 font-medium text-xs">{t.name}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{t.provider || "—"}</td>
                        <td className="px-3 py-2 text-xs font-mono">{t.issue_date || "—"}</td>
                        <td className="px-3 py-2 text-xs font-mono">{t.expiry_date || "—"}</td>
                        <td className="px-3 py-2 text-center"><Badge variant="outline" className={`text-[10px] rounded-full ${TRAINING_TONE[t.status] || ""}`}>{t.status}</Badge></td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {t.certificate ? (
                              <Button asChild size="icon" variant="ghost" className="h-7 w-7" title="Download certificate" data-testid={`emp-training-cert-${t.id}`}>
                                <a href={endpoints.certificateUrl(employeeId, t.id)} target="_blank" rel="noreferrer"><FileText className="h-3.5 w-3.5 text-emerald-600" /></a>
                              </Button>
                            ) : null}
                            <Button size="icon" variant="ghost" className="h-7 w-7" title="Upload certificate" data-testid={`emp-training-upload-${t.id}`} onClick={() => { setUploadingCertFor(t.id); certFileRef.current?.click(); }}>
                              <Upload className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => removeTraining(t.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {trainings.length === 0 && <tr><td colSpan="6" className="text-center py-4 text-xs text-slate-500">No training records yet.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="rounded-2xl border border-dashed border-slate-300 dark:border-white/10 p-3 grid grid-cols-4 gap-2 items-end">
                <div className="col-span-2"><Label className="text-[10px]">Training name</Label><Input data-testid="emp-newtraining-name" className="h-8" value={newTraining.name} onChange={(e) => setNewTraining({ ...newTraining, name: e.target.value })} placeholder="e.g. First Aid" /></div>
                <div className="col-span-2"><Label className="text-[10px]">Provider</Label><Input className="h-8" value={newTraining.provider} onChange={(e) => setNewTraining({ ...newTraining, provider: e.target.value })} /></div>
                <div><Label className="text-[10px]">Issue date</Label><Input className="h-8" type="date" data-testid="emp-newtraining-issue" value={newTraining.issue_date} onChange={(e) => setNewTraining({ ...newTraining, issue_date: e.target.value })} /></div>
                <div><Label className="text-[10px]">Expiry date</Label><Input className="h-8" type="date" data-testid="emp-newtraining-expiry" value={newTraining.expiry_date} onChange={(e) => setNewTraining({ ...newTraining, expiry_date: e.target.value })} /></div>
                <div className="col-span-2"><Button data-testid="emp-newtraining-add" type="button" size="sm" className="w-full brand-gradient text-white" onClick={addTraining}><Plus className="h-3.5 w-3.5 mr-1" /> Add training</Button></div>
              </div>
            </div>
          )}

          {tab === "qualifications" && isEdit && loaded && (
            <div className="space-y-2">
              {(loaded.qualifications || []).map((q, i) => (
                <div key={i} className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-white/10 p-2">
                  <GraduationCap className="h-4 w-4 text-slate-400" />
                  <div className="flex-1 text-xs"><span className="font-semibold">{q.name}</span> · {q.institution} {q.year && `· ${q.year}`}</div>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => removeQual(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <div className="grid grid-cols-4 gap-2 pt-2 border-t border-slate-100 dark:border-white/5 items-end">
                <div className="col-span-2"><Label className="text-[10px]">Qualification</Label><Input className="h-8" value={newQual.name} onChange={(e) => setNewQual({ ...newQual, name: e.target.value })} /></div>
                <div><Label className="text-[10px]">Institution</Label><Input className="h-8" value={newQual.institution} onChange={(e) => setNewQual({ ...newQual, institution: e.target.value })} /></div>
                <div><Label className="text-[10px]">Year</Label><Input className="h-8" type="number" value={newQual.year} onChange={(e) => setNewQual({ ...newQual, year: e.target.value })} /></div>
                <div className="col-span-4"><Button type="button" size="sm" className="brand-gradient text-white" onClick={addQual}><Plus className="h-3.5 w-3.5 mr-1" /> Add qualification</Button></div>
              </div>
            </div>
          )}

          {tab === "appointments" && isEdit && loaded && (
            <div className="space-y-2">
              {(loaded.legal_appointments || []).map((a, i) => (
                <div key={i} className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-white/10 p-2">
                  <ShieldCheck className="h-4 w-4 text-slate-400" />
                  <div className="flex-1 text-xs"><span className="font-semibold">{a.role}</span> {a.reference && `(${a.reference})`} {a.appointed_date && `· ${a.appointed_date}`}</div>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => removeAppt(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 dark:border-white/5 items-end">
                <div><Label className="text-[10px]">Role</Label><Input className="h-8" value={newAppt.role} onChange={(e) => setNewAppt({ ...newAppt, role: e.target.value })} /></div>
                <div><Label className="text-[10px]">Reference</Label><Input className="h-8" value={newAppt.reference} onChange={(e) => setNewAppt({ ...newAppt, reference: e.target.value })} /></div>
                <div><Label className="text-[10px]">Appointed</Label><Input className="h-8" type="date" value={newAppt.appointed_date} onChange={(e) => setNewAppt({ ...newAppt, appointed_date: e.target.value })} /></div>
                <div className="col-span-3"><Button type="button" size="sm" className="brand-gradient text-white" onClick={addAppt}><Plus className="h-3.5 w-3.5 mr-1" /> Add appointment</Button></div>
              </div>
            </div>
          )}

          {tab === "kpis" && isEdit && loaded && (
            <div className="space-y-2">
              {(loaded.kpis || []).map((k, i) => (
                <div key={i} className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-white/10 p-2">
                  <Briefcase className="h-4 w-4 text-slate-400" />
                  <div className="flex-1 text-xs"><span className="font-semibold">{k.name}</span> · Target {k.target} · Current {k.current}</div>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => removeKpi(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 dark:border-white/5 items-end">
                <div><Label className="text-[10px]">KPI name</Label><Input className="h-8" value={newKpi.name} onChange={(e) => setNewKpi({ ...newKpi, name: e.target.value })} /></div>
                <div><Label className="text-[10px]">Target</Label><Input className="h-8" value={newKpi.target} onChange={(e) => setNewKpi({ ...newKpi, target: e.target.value })} /></div>
                <div><Label className="text-[10px]">Current</Label><Input className="h-8" value={newKpi.current} onChange={(e) => setNewKpi({ ...newKpi, current: e.target.value })} /></div>
                <div className="col-span-3"><Button type="button" size="sm" className="brand-gradient text-white" onClick={addKpi}><Plus className="h-3.5 w-3.5 mr-1" /> Add KPI</Button></div>
              </div>
            </div>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
